import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { formatInTimeZone } from 'date-fns-tz';
import {
  bordesDelEstudio, listCierresMercado, listFichasMercado, listFichasModerno,
} from '../../api/pg';
import type { CierreMercado, FichaMercado, FichaModerno } from '../../domain/pg/tipos';
import { nombreDeTienda } from '../../domain/pg/tipos';
import {
  agruparMercados, agruparPersonas, claveTexto, STOPWORDS_LUGAR, STOPWORDS_PERSONA,
  type GrupoMercado,
} from '../../domain/pg/normalizar';
import { coincideTodasLasPalabras } from '../../domain/tableFilters';
import RegistrosPg from './pg/RegistrosPg';
import ResumenPg from './pg/ResumenPg';

const TZ = 'America/Lima';
const hoyLima = () => formatInTimeZone(new Date(), TZ, 'yyyy-MM-dd');

export type Instrumento = 'mercados' | 'moderno';

/**
 * Registros de campo · **Levantamiento P&G**.
 *
 * ⚠️ **No es el levantamiento de SOFTYS.** Hay dos estudios que se llaman igual: el de Softys son
 * las once tablas `levantamiento_*`, está en pausa desde agosto y se ve en `/panel/levantamiento`.
 * Este es el de P&G y sale de `mer_fichas`, `mer_cierres` y `pg_fichas`. El 15-set un agente
 * conectado a la base leyó el estudio equivocado y contestó con total seguridad; por eso el rótulo,
 * la ruta y la bajada de esta pantalla dicen «P&G» en todas partes.
 *
 * ## Para qué existe
 *
 * El jefe de operaciones tenía que pedirle esto a un agente conectado a la base, y cada pregunta le
 * costaba tokens: *«le está costando pedir a su claude la información que necesita»*. Un dato que ya
 * está en la base no debería pasar por un modelo para poder mirarse. Acá se contesta sin red de por
 * medio y sin costo.
 *
 * ## Las dos solapas son la MISMA lista
 *
 * 🔴 «Resumen» se calcula con `useMemo` sobre **exactamente las filas que muestra «Registros»**, no
 * con una segunda consulta. Dos caminos de datos para la misma pregunta terminan dando dos números,
 * y cuando eso pasa quien mira deja de creerle a los dos y vuelve a contar a mano. Por eso arriba va
 * siempre la línea de reconciliación: cuántas de cuántas, con qué filtro.
 */
export default function LevantamientoPgBandeja() {
  const [instrumento, setInstrumento] = useState<Instrumento>('mercados');
  const [solapa, setSolapa] = useState<'registros' | 'resumen'>('registros');

  const [desde, setDesde] = useState('');
  const [hasta, setHasta] = useState(hoyLima);
  const [bordes, setBordes] = useState<{ primera: string | null; ultima: string | null; total: number } | null>(null);

  const [fichasMer, setFichasMer] = useState<FichaMercado[]>([]);
  const [cierresMer, setCierresMer] = useState<CierreMercado[]>([]);
  const [fichasMod, setFichasMod] = useState<FichaModerno[]>([]);
  const [cargando, setCargando] = useState(false);
  const [error, setError] = useState('');

  const [sitio, setSitio] = useState('');
  const [persona, setPersona] = useState('');
  const [busqueda, setBusqueda] = useState('');
  const [soloSenal, setSoloSenal] = useState(false);
  const [soloNoLevantados, setSoloNoLevantados] = useState(false);

  /**
   * El «desde» por omisión es la fecha de la PRIMERA ficha del instrumento, no el 1 del mes.
   *
   * 🔴 Es lo que evita el peor de los vacíos: uno que no se ve. Abriendo en el mes en curso, canal
   * moderno mostraría «20 de 20 fichas» —cierto y completo dentro del rango— y dejaría afuera las
   * 4 del 29-ago, que son las únicas de Makro y traen las góndolas más anchas del estudio. Un total
   * honesto dentro de un rango que nadie eligió es la forma más fácil de esconder media muestra.
   */
  useEffect(() => {
    let vivo = true;
    setBordes(null);
    void bordesDelEstudio(instrumento === 'mercados' ? 'mer_fichas' : 'pg_fichas')
      .then((b) => {
        if (!vivo) return;
        setBordes(b);
        setDesde(b.primera ?? hoyLima());
        setHasta(b.ultima !== null && b.ultima > hoyLima() ? b.ultima : hoyLima());
      })
      .catch((e: unknown) => {
        if (vivo) setError(e instanceof Error ? e.message : 'No se pudo leer el rango del estudio.');
      });
    return () => { vivo = false; };
  }, [instrumento]);

  /**
   * Cada carga lleva su número y solo la ÚLTIMA escribe (30-set, el mismo arreglo de la bandeja de
   * Yichang). Al cambiar de instrumento salen dos: una con el rango del instrumento anterior —el efecto
   * corre antes de que lleguen los bordes nuevos— y otra con el rango nuevo. Si la primera tardaba más,
   * pisaba a la segunda y la pantalla mostraba fichas de otro rango que el escrito arriba, sin error.
   */
  const pedido = useRef(0);
  const cargar = useCallback(async () => {
    if (desde === '' || hasta === '') return;
    if (desde > hasta) { setError('El rango de fechas está invertido.'); return; }
    const este = ++pedido.current;
    const vigente = () => este === pedido.current;
    setCargando(true);
    setError('');
    try {
      if (instrumento === 'mercados') {
        const [f, c] = await Promise.all([
          listFichasMercado(desde, hasta), listCierresMercado(desde, hasta),
        ]);
        if (!vigente()) return;
        setFichasMer(f);
        setCierresMer(c);
        setFichasMod([]);
      } else {
        const f = await listFichasModerno(desde, hasta);
        if (!vigente()) return;
        setFichasMod(f);
        setFichasMer([]);
        setCierresMer([]);
      }
    } catch (e) {
      if (!vigente()) return;
      // El mensaje del servidor va primero y tal cual. Reemplazarlo por uno propio ya costó
      // desplegar una función de sonda aparte para recuperar algo que ya se tenía.
      setError(e instanceof Error ? e.message : 'No se pudieron cargar las fichas.');
    } finally {
      if (vigente()) setCargando(false);
    }
  }, [instrumento, desde, hasta]);

  useEffect(() => { void cargar(); }, [cargar]);

  // Al cambiar de instrumento los filtros de la segunda barra dejan de tener sentido: los mercados
  // no son tiendas y la lista de supervisores es otra. Dejarlos puestos esconde todo sin decir por
  // qué —el vacío más caro de explicar—.
  useEffect(() => { setSitio(''); setPersona(''); setBusqueda(''); }, [instrumento]);

  // ── Normalización, una sola vez por carga ────────────────────────────────

  const gruposMercado = useMemo<GrupoMercado[]>(
    () => (instrumento === 'mercados'
      ? agruparMercados(fichasMer.map((f) => ({ crudo: f.mercadoNombre, lat: f.lat, lng: f.lng })))
      : []),
    [instrumento, fichasMer],
  );

  /** En canal moderno el «sitio» es la tienda, y ahí NO hace falta normalizar: `cadena` viene del
   *  catálogo o no viene, y el nombre sale del maestro cuando existe. Se agrupa por el nombre que
   *  se muestra, que es lo que el usuario está mirando. */
  const tiendas = useMemo(() => {
    const m = new Map<string, number>();
    for (const f of fichasMod) {
      const n = nombreDeTienda(f);
      m.set(n, (m.get(n) ?? 0) + 1);
    }
    return [...m.entries()].map(([nombre, n]) => ({ nombre, n }))
      .sort((a, b) => b.n - a.n || a.nombre.localeCompare(b.nombre, 'es'));
  }, [fichasMod]);

  const personas = useMemo(
    () => agruparPersonas(instrumento === 'mercados'
      ? fichasMer.map((f) => f.supervisor)
      : fichasMod.map((f) => f.supervisor)),
    [instrumento, fichasMer, fichasMod],
  );

  /** De qué grupo es cada ficha de mercados. Se resuelve por clave, no por texto. */
  const grupoDeClave = useMemo(() => {
    const m = new Map<string, GrupoMercado>();
    for (const g of gruposMercado) if (!m.has(g.clave)) m.set(g.clave, g);
    return m;
  }, [gruposMercado]);

  // ── El filtro de la segunda barra: recorta lo ya traído ──────────────────

  const merVisibles = useMemo(() => fichasMer.filter((f) => {
    if (soloNoLevantados && f.levantado) return false;
    if (soloSenal && f.flags.length === 0) return false;
    if (sitio !== '' && claveTexto(f.mercadoNombre, STOPWORDS_LUGAR) !== sitio) return false;
    if (persona !== '' && claveTexto(f.supervisor, STOPWORDS_PERSONA) !== persona) return false;
    // El pajar NO incluye `zona` ni `distrito`: en las fichas de provincia esos dos traen un valor
    // de Lima que se autocompletó desde la lista cerrada. Buscar «Surquillo» devolvería una ficha
    // de Tumbes, y quien la vea va a creer que hay data de Surquillo.
    return coincideTodasLasPalabras(
      busqueda, f.mercadoNombre, f.supervisor, f.puestoNombre,
      f.puestoNumero === null ? null : String(f.puestoNumero),
      grupoDeClave.get(claveTexto(f.mercadoNombre, STOPWORDS_LUGAR) ?? '')?.rotulo,
    );
  }), [fichasMer, sitio, persona, busqueda, soloSenal, soloNoLevantados, grupoDeClave]);

  const modVisibles = useMemo(() => fichasMod.filter((f) => {
    if (soloSenal && f.flags.length === 0) return false;
    if (sitio !== '' && nombreDeTienda(f) !== sitio) return false;
    if (persona !== '' && claveTexto(f.supervisor, STOPWORDS_PERSONA) !== persona) return false;
    return coincideTodasLasPalabras(
      busqueda, nombreDeTienda(f), f.tiendaTexto, f.cadena, f.supervisor,
    );
  }), [fichasMod, sitio, persona, busqueda, soloSenal]);

  const traidas = instrumento === 'mercados' ? fichasMer.length : fichasMod.length;
  const visibles = instrumento === 'mercados' ? merVisibles.length : modVisibles.length;
  const hayFiltro = sitio !== '' || persona !== '' || busqueda.trim() !== ''
    || soloSenal || soloNoLevantados;

  const fueraDelRango = bordes === null ? 0 : Math.max(0, bordes.total - traidas);

  const limpiar = () => {
    setSitio(''); setPersona(''); setBusqueda('');
    setSoloSenal(false); setSoloNoLevantados(false);
  };

  return (
    <div>
      {/* Subtítulo (h3) y no título: desde el 30-set esta pantalla vive dentro de «Levantamiento de
          información», que tiene el h2 y el selector de cliente. Nada más cambió. */}
      <h3 className="mb-1 text-base font-semibold text-gray-900">Levantamiento P&amp;G</h3>
      <p className="mb-4 max-w-3xl text-sm text-gray-600">
        Canal tradicional (mercados) y canal moderno (góndola) del estudio de <b>P&amp;G</b>.
        Cada fila es una ficha de campo y se abre para ver todo lo que se levantó, con sus fotos.
        {' '}
        <span className="text-gray-500">
          No es el levantamiento de Softys, que es otro estudio y está en pausa desde agosto.
        </span>
      </p>

      {/* Primera barra: lo que viaja al servidor. */}
      <div className="mb-2 flex flex-wrap items-end gap-3">
        <label className="text-sm">
          Instrumento
          <select
            aria-label="Instrumento"
            value={instrumento}
            onChange={(e) => setInstrumento(e.target.value as Instrumento)}
            className="mt-1 block rounded border px-2 py-1.5"
          >
            <option value="mercados">Mercados (canal tradicional)</option>
            <option value="moderno">Canal moderno (góndola)</option>
          </select>
        </label>
        <label className="text-sm">
          Desde
          <input type="date" value={desde} onChange={(e) => setDesde(e.target.value)}
            className="mt-1 block rounded border px-2 py-1.5" />
        </label>
        <label className="text-sm">
          Hasta
          <input type="date" value={hasta} onChange={(e) => setHasta(e.target.value)}
            className="mt-1 block rounded border px-2 py-1.5" />
        </label>
        <button type="button" onClick={() => void cargar()} disabled={cargando}
          className="rounded border border-gray-300 px-3 py-1.5 text-sm">
          {cargando ? 'Cargando…' : 'Actualizar'}
        </button>
        {bordes !== null && bordes.primera !== null && (
          <span className="pb-1.5 text-xs text-gray-500">
            El estudio va del {bordes.primera} al {bordes.ultima}
            {fueraDelRango > 0 && (
              <>
                {' · '}
                <b className="text-amber-700">
                  {fueraDelRango} {fueraDelRango === 1 ? 'ficha' : 'fichas'} fuera del rango
                </b>
              </>
            )}
          </span>
        )}
      </div>

      {/* Segunda barra: recorta lo ya traído, sin volver al servidor. */}
      <div className="mb-4 flex flex-wrap items-end gap-3 border-t border-gray-100 pt-3">
        <label className="text-sm">
          {instrumento === 'mercados' ? 'Mercado' : 'Tienda'}
          <select
            aria-label={instrumento === 'mercados' ? 'Mercado' : 'Tienda'}
            value={sitio} onChange={(e) => setSitio(e.target.value)}
            className="mt-1 block max-w-xs rounded border px-2 py-1.5"
          >
            <option value="">Todos</option>
            {instrumento === 'mercados'
              ? gruposMercado.map((g) => (
                <option key={g.clave} value={g.clave}>
                  {g.rotulo} · {g.n}
                  {g.grafias.length > 1 ? ` (${g.grafias.length} grafías)` : ''}
                  {g.sinNormalizar ? ' · sin normalizar' : ''}
                </option>
              ))
              : tiendas.map((t) => (
                <option key={t.nombre} value={t.nombre}>{t.nombre} · {t.n}</option>
              ))}
          </select>
        </label>
        <label className="text-sm">
          Supervisor
          <select aria-label="Supervisor" value={persona} onChange={(e) => setPersona(e.target.value)}
            className="mt-1 block max-w-xs rounded border px-2 py-1.5">
            <option value="">Todos</option>
            {personas.map((p) => (
              <option key={p.clave} value={p.clave}>
                {p.rotulo} · {p.n}{p.grafias.length > 1 ? ` (${p.grafias.length} grafías)` : ''}
              </option>
            ))}
          </select>
        </label>
        <label className="text-sm">
          Buscar
          <input type="search" aria-label="Buscar" value={busqueda}
            onChange={(e) => setBusqueda(e.target.value)}
            placeholder={instrumento === 'mercados' ? 'Ej.: moshoqueque pilar' : 'Ej.: wong angela'}
            className="mt-1 block w-64 rounded border px-2 py-1.5" />
        </label>
        <label className="flex items-center gap-2 pb-1.5 text-sm">
          <input type="checkbox" checked={soloSenal} onChange={(e) => setSoloSenal(e.target.checked)} />
          Solo con alguna señal
        </label>
        {instrumento === 'mercados' && (
          <label className="flex items-center gap-2 pb-1.5 text-sm">
            <input type="checkbox" checked={soloNoLevantados}
              onChange={(e) => setSoloNoLevantados(e.target.checked)} />
            Solo los que no se pudieron levantar
          </label>
        )}
      </div>

      {error !== '' && (
        <p role="alert" className="my-3 rounded border border-red-200 bg-red-50 p-3 text-sm text-red-900">
          {error}
        </p>
      )}

      {/* La línea de reconciliación. Va SIEMPRE, con filtro y sin filtro: es el único control que
          se puede hacer de un vistazo —que la suma de los grupos dé igual al total— y sin ella un
          resumen filtrado se lee como el resumen de todo. */}
      <p className="mb-3 rounded border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-gray-700">
        <b>{visibles}</b> de <b>{traidas}</b> fichas traídas
        {' · '}{instrumento === 'mercados' ? 'mercados' : 'canal moderno'}
        {' · '}{desde} a {hasta}
        {hayFiltro && (
          <>
            {' · con filtro '}
            <button type="button" onClick={limpiar} className="text-blue-700 underline">
              limpiar
            </button>
          </>
        )}
      </p>

      {/* Render CONDICIONAL y no `hidden`: el navegador trae `[hidden]{display:none}` en su hoja de
          usuario-agente y cualquier `display` propio le gana, así que una solapa «oculta» con
          `hidden` se queda dibujada. Ya pasó dos veces en el panel de Vital. */}
      <div className="mb-4 flex flex-wrap gap-1 border-b border-gray-200">
        {([['registros', 'Registros'], ['resumen', 'Resumen']] as const).map(([k, label]) => (
          <button
            key={k} type="button" onClick={() => setSolapa(k)}
            aria-current={solapa === k ? 'page' : undefined}
            className={`-mb-px border-b-2 px-4 py-3 text-sm ${solapa === k
              ? 'border-blue-600 font-semibold text-blue-700'
              : 'border-transparent text-gray-600 hover:border-gray-300 hover:text-gray-900'}`}
          >
            {label} · {instrumento === 'mercados' ? 'mercados' : 'canal moderno'}
          </button>
        ))}
      </div>

      {solapa === 'registros' ? (
        <RegistrosPg
          instrumento={instrumento}
          mercados={merVisibles}
          moderno={modVisibles}
          cierres={cierresMer}
          grupoDeClave={grupoDeClave}
          cargando={cargando}
          hayFiltro={hayFiltro}
          onLimpiar={limpiar}
        />
      ) : (
        <ResumenPg
          instrumento={instrumento}
          mercados={merVisibles}
          moderno={modVisibles}
          cierres={cierresMer}
          gruposMercado={gruposMercado}
        />
      )}
    </div>
  );
}
